import { useEffect, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Alert, Input } from '@iveco-ti/iveco-ds'
import { FormProvider, useForm } from '@iveco-ti/iveco-ds/hook-form'
import { AlertTriangle, Eye, EyeOff, Info } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { AppData, StepInfo } from '../../models/applicationConnection'
import {
  ExistingUserPasswordForm,
  ExistingUserPasswordSchema,
  NewUserPasswordForm,
  NewUserPasswordSchema,
} from './schemas/PasswordsStepSchema'
import { StepWrapper } from './StepWrapper'

type PasswordsStepProps = {
  appData: AppData
  stepInfo: StepInfo
  hasActivationFlow?: boolean
  onValidationChange?: (isValid: boolean) => void
  onFormChange?: (values: unknown) => void
  customLabel?: string
  customPlaceholder?: string
  customInfoMessage?: string
  customDescription?: string
}

export function PasswordsStep({
  appData,
  stepInfo,
  hasActivationFlow,
  onValidationChange,
  onFormChange,
  customLabel,
  customPlaceholder,
  customInfoMessage,
  customDescription,
}: PasswordsStepProps) {
  const { t } = useTranslation('common')
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showApplicationPassword, setShowApplicationPassword] = useState(false)
  const [showServiceSolutionsPassword, setShowServiceSolutionsPassword] =
    useState(false)

  const newUserHookForm = useForm<NewUserPasswordForm>({
    resolver: zodResolver(NewUserPasswordSchema()),
    mode: 'onChange',
    defaultValues: {
      newPassword: '',
    },
  })

  const existingUserHookForm = useForm<ExistingUserPasswordForm>({
    resolver: zodResolver(ExistingUserPasswordSchema()),
    mode: 'onChange',
    defaultValues: {
      applicationPassword: '',
      serviceSolutionsPassword: '',
    },
  })

  const currentForm = hasActivationFlow ? newUserHookForm : existingUserHookForm
  const isFormValid = currentForm.formState.isValid
  const formValues = currentForm.watch()

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isFormValid)
    }
  }, [isFormValid, onValidationChange])

  useEffect(() => {
    if (onFormChange) {
      onFormChange(formValues)
    }
  }, [formValues, onFormChange])

  if (hasActivationFlow) {
    const label = customLabel || t('app_connection.passwords.new_user.label')
    const placeholder =
      customPlaceholder || t('app_connection.passwords.new_user.placeholder')
    const infoMessage =
      customInfoMessage ||
      t('app_connection.passwords.new_user.info_message', {
        applicationName: appData.applicationName,
      })

    return (
      <FormProvider {...newUserHookForm}>
        <StepWrapper appData={appData} stepInfo={stepInfo}>
          <div className="space-y-4">
            <div className="text-sm text-gray-600 mb-4">
              {customDescription ||
                t('app_connection.passwords.new_user.description')}
            </div>

            <div className="w-[50%]">
              <Input.Icon
                size="lg"
                name="newPassword"
                label={label}
                type={showNewPassword ? 'text' : 'password'}
                placeholder={placeholder}
                icon={
                  showNewPassword ? (
                    <Eye
                      className="text-base-500 mt-2"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    />
                  ) : (
                    <EyeOff
                      className="text-base-500 mt-2"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    />
                  )
                }
                iconPosition="right"
              />
            </div>
            <Alert.Root>
              <Alert.Icon icon={<Info />} />
              <Alert.Body>
                <Alert.Description>{infoMessage}</Alert.Description>
              </Alert.Body>
            </Alert.Root>
          </div>
        </StepWrapper>
      </FormProvider>
    )
  }

  return (
    <FormProvider {...existingUserHookForm}>
      <StepWrapper appData={appData} stepInfo={stepInfo}>
        <div className="space-y-4">
          <div className="text-sm text-gray-600 mb-4">
            {customDescription ||
              t('app_connection.passwords.existing_user.description')}
          </div>

          <div className="w-[30%] space-y-4">
            <Input.Icon
              size="lg"
              name="applicationPassword"
              label={t(
                'app_connection.passwords.existing_user.application_label',
                {
                  applicationName: appData.applicationName,
                },
              )}
              type={showApplicationPassword ? 'text' : 'password'}
              placeholder={t(
                'app_connection.passwords.existing_user.application_placeholder',
                {
                  applicationName: appData.applicationName,
                },
              )}
              icon={
                showApplicationPassword ? (
                  <Eye
                    className="text-base-500 mt-2"
                    onClick={() =>
                      setShowApplicationPassword(!showApplicationPassword)
                    }
                  />
                ) : (
                  <EyeOff
                    className="text-base-500 mt-2"
                    onClick={() =>
                      setShowApplicationPassword(!showApplicationPassword)
                    }
                  />
                )
              }
              iconPosition="right"
            />

            <Input.Icon
              size="lg"
              name="serviceSolutionsPassword"
              label={t(
                'app_connection.passwords.existing_user.service_solutions_label',
              )}
              type={showServiceSolutionsPassword ? 'text' : 'password'}
              placeholder={t(
                'app_connection.passwords.existing_user.service_solutions_placeholder',
              )}
              icon={
                showServiceSolutionsPassword ? (
                  <Eye
                    className="text-base-500 mt-2"
                    onClick={() =>
                      setShowServiceSolutionsPassword(
                        !showServiceSolutionsPassword,
                      )
                    }
                  />
                ) : (
                  <EyeOff
                    className="text-base-500 mt-2"
                    onClick={() =>
                      setShowServiceSolutionsPassword(
                        !showServiceSolutionsPassword,
                      )
                    }
                  />
                )
              }
              iconPosition="right"
            />
          </div>

          <Alert.Root>
            <Alert.Icon icon={<AlertTriangle />} />
            <Alert.Body>
              <Alert.Description>
                {t('app_connection.passwords.existing_user.warning_message', {
                  applicationName: appData.applicationName,
                })}
              </Alert.Description>
            </Alert.Body>
          </Alert.Root>
        </div>
      </StepWrapper>
    </FormProvider>
  )
}
